import { createApi } from '@reduxjs/toolkit/query/react';

import { log } from '@/app/shared/utils/logger';
import { ApiResponse, extractData } from '@app/store/utils/common';
import { invalidateOnSuccess } from '@app/shared/utils/common';
import {
  TotalUsers,
  CurrentUser,
  UpdateLanguagePayload,
  CurrentUserProfile,
  CurrentUserProfileUpdatePayload,
  UpdateUserAutoPurchasePayload,
  UsersTagType,
  SendGiftResponse,
  SendGiftBody,
  SpinWheelResponse,
  ClaimMediaRewardBody,
  UpdateUserFeedPandaNtfPayload,
  PracticeRewardData,
} from './usersTypes';
import { stonesApi } from '@app/store/slices/stones/stonesApi';
import { StoneTagType } from '@app/store/slices/stones/stoneTypes';
import { settlementsApi } from '@app/store/slices/settlements/settlementsApi';
import { SettlementTagType } from '@app/store/slices/settlements/settlementsTypes';
import { tasksApi } from '@app/store/slices/tasks/tasksApi';
import { TaskTagTypes } from '@app/store/slices/tasks/taskTypes';
import { contentApi } from '@app/store/slices/content/contentApi';
import { ContentTagType } from '@app/store/slices/content/contentTypes';
import { updateBalance, updateCurrentXpFromResponse } from '@app/store/utils/cacheUpdate';
import { BalanceActionType, PriceType } from '@app/shared/types/common';
import { balanceApi } from '@app/store/slices/balance/balanceApi';
import { userProgressApi } from '@app/store/slices/userProgress/userProgressApi';
import { UserProgressTagType } from '@app/store/slices/userProgress/userProgressTypes';
import { BalanceTagType } from '@app/store/slices/balance/balanceTypes';
import { baseQueryWithReauth } from '@/app/store/utils/baseQueryWithReauth';
import { setLogContext } from '@/app/shared/utils/get-log-data';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(UsersTagType),
  endpoints: (builder) => ({
    getTotalUsers: builder.query<TotalUsers, void>({
      query: () => '/users/total',
    }),
    getCurrentUser: builder.query<CurrentUser | null, void>({
      query: () => '/users/me',
      transformResponse: extractData,
      providesTags: [UsersTagType.CURRENT_USER],
      onQueryStarted(_, { queryFulfilled }) {
        void queryFulfilled.then(({ data }) => {
          setLogContext({ userId: data?.id });
        });
      },
    }),
    getCurrentUserProfile: builder.query<CurrentUserProfile | null, void>({
      query: () => '/users/me/profile',
      transformResponse: extractData,
      providesTags: [UsersTagType.CURRENT_USER_PROFILE],
    }),
    getUserProfile: builder.query<CurrentUserProfile | null, number>({
      query: (userId) => `/users/profile/${String(userId)}`,
      transformResponse: extractData,
    }),
    receiveGift: builder.mutation<{ message: string } | null, string>({
      query: (giftToken) => ({
        url: `/users/me/gift/receive?giftToken=${giftToken}`,
        method: 'GET',
      }),
      transformResponse: extractData,
      onQueryStarted(_, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          dispatch(settlementsApi.util.invalidateTags([SettlementTagType.SETTLEMENTS]));
        });
      },
    }),
    sendGift: builder.mutation<ApiResponse<SendGiftResponse>, SendGiftBody>({
      query: (body) => ({
        url: '/users/me/gift/send',
        method: 'POST',
        body,
      }),
    }),
    claimFirstStoneReward: builder.mutation({
      query: () => ({
        url: '/users/me/claim-first-stone-reward',
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess([UsersTagType.CURRENT_USER_PROFILE]),
      onQueryStarted(reward: number, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          updateBalance(PriceType.COOKIE, BalanceActionType.INCREASE, reward, dispatch);
        });
      },
    }),
    spinWheelReward: builder.mutation<ApiResponse<SpinWheelResponse>, void>({
      query: () => ({
        url: '/users/me/wheel-of-fortune/spin',
        method: 'POST',
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data: resp } = await queryFulfilled;

          if (!resp.ok) {
            return;
          }

          const reward = resp.data;
          const rewardType = reward?.rewardType;
          const amount = reward?.rewardAmount ?? 0;

          if (!rewardType || amount <= 0) {
            return;
          }

          // cookie_* => cookie balance
          if (rewardType.startsWith('cookie_')) {
            updateBalance(PriceType.COOKIE, BalanceActionType.INCREASE, amount, dispatch);
            return;
          }

          // diamond_* => TON balance
          if (rewardType.startsWith('diamond_')) {
            updateBalance(PriceType.TON, BalanceActionType.INCREASE, amount, dispatch);
            return;
          }

          // Other types (card/tent/land/...) do not change the balance
        } catch (error: unknown) {
          // the request failed or was canceled
          void log({ level: 'error', message: 'SpinWheelReward error', error });
        }
      },
    }),
    claimMediaReward: builder.mutation<ApiResponse<PracticeRewardData>, ClaimMediaRewardBody>({
      query: (body) => ({
        url: '/users/me/practice-reward',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        await updateCurrentXpFromResponse(dispatch, queryFulfilled);
      },
    }),
    updateLanguage: builder.mutation<ApiResponse<CurrentUser>, UpdateLanguagePayload>({
      query: (body) => ({
        url: '/users/me/language',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        UsersTagType.CURRENT_USER,
        UsersTagType.CURRENT_USER_PROFILE,
      ]),
      onQueryStarted(_, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          dispatch(stonesApi.util.invalidateTags([StoneTagType.STONES]));
          dispatch(settlementsApi.util.invalidateTags([SettlementTagType.SETTLEMENTS]));
          dispatch(tasksApi.util.invalidateTags([TaskTagTypes.TASKS]));
          dispatch(
            contentApi.util.invalidateTags([
              ContentTagType.CARD,
              ContentTagType.RANDOM_CARDS,
              ContentTagType.DAILY_TIP,
              ContentTagType.SUPER,
            ])
          );
        });
      },
    }),
    updateCurrentUserProfile: builder.mutation<
      ApiResponse<CurrentUserProfile>,
      CurrentUserProfileUpdatePayload
    >({
      query: (body) => ({
        url: '/users/me/profile',
        method: 'PATCH',
        body,
      }),
      onQueryStarted(_, { queryFulfilled, dispatch }) {
        void queryFulfilled.then((userProfile) => {
          if (userProfile.data.ok && userProfile.data.data) {
            void dispatch(
              usersApi.util.upsertQueryData(
                'getCurrentUserProfile',
                undefined,
                userProfile.data.data
              )
            );
          }
        });
      },
    }),
    updateUserAutoPurchase: builder.mutation<
      ApiResponse<CurrentUser>,
      UpdateUserAutoPurchasePayload
    >({
      query: (body) => ({
        url: '/users/me/auto-purchase',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [UsersTagType.CURRENT_USER],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        void queryFulfilled.then(() => {
          dispatch(settlementsApi.util.invalidateTags([SettlementTagType.SETTLEMENTS]));
          dispatch(
            userProgressApi.util.invalidateTags([UserProgressTagType.CURRENT_USER_PROGRESS])
          );
          dispatch(balanceApi.util.invalidateTags([BalanceTagType.BALANCE]));
        });
      },
    }),
    updateUserFeedPandaNtf: builder.mutation<
      ApiResponse<CurrentUserProfile>,
      UpdateUserFeedPandaNtfPayload
    >({
      query: (body) => ({
        url: '/users/me/feed-panda-ntf',
        method: 'PATCH',
        body,
      }),
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        void queryFulfilled.then((userProfile) => {
          if (userProfile.data.ok && userProfile.data.data) {
            void dispatch(
              usersApi.util.upsertQueryData(
                'getCurrentUserProfile',
                undefined,
                userProfile.data.data
              )
            );
          }
        });
      },
    }),
  }),
});

export const {
  useGetTotalUsersQuery,
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useUpdateLanguageMutation,
  useGetCurrentUserProfileQuery,
  useUpdateCurrentUserProfileMutation,
  useUpdateUserAutoPurchaseMutation,
  useGetUserProfileQuery,
  useSendGiftMutation,
  useReceiveGiftMutation,
  useClaimFirstStoneRewardMutation,
  useSpinWheelRewardMutation,
  useClaimMediaRewardMutation,
} = usersApi;
